/* eslint-disable multiline-comment-style */
import type { PreviewError } from '~/lib/stores/previews';

export interface GroupedError {
  representativeError: PreviewError;
  count: number;
}

export const groupAndCountErrors = (errors: PreviewError[] | null): GroupedError[] => {
  if (!errors || errors.length === 0) {
    return [];
  }

  const errorMap = new Map<string, { representativeError: PreviewError; count: number }>();

  for (const error of errors) {
    const key = error.message.trim(); // Simple key: trimmed message
    /*
     * For a regex approach, you would generate the key using your regex here.
     * const key = extractKeyWithRegex(error.message);
     */

    if (errorMap.has(key)) {
      const existingGroup = errorMap.get(key)!;
      existingGroup.count++;

      // Concatenate stack traces
      // Concatenate stack traces (assuming error.stack always exists)
      // And assuming existingGroup.representativeError.stack was set on the first occurrence.
      existingGroup.representativeError.stack += `\n\n--- (Additional Occurrence Stack) ---\n\n${error.stack}`;
    } else {
      // When creating a new group, make a copy of the error to avoid mutating the original.
      // error.stack is assumed to exist.
      const representativeErrorCopy: PreviewError = {
        ...error,
        stack: error.stack, // Explicitly copy the stack
      };
      errorMap.set(key, { representativeError: representativeErrorCopy, count: 1 });
    }
  }

  return Array.from(errorMap.values());
};

export const getCombinedErrorTextForCopy = (grouped: GroupedError[]): string => {
  if (!grouped || grouped.length === 0) {
    return 'No errors to display.';
  }

  return grouped
    .map((group, index) => {
      const { representativeError, count } = group;
      const details = `Unique Error ${index + 1} (occurred ${count} time${count > 1 ? 's' : ''}):\nMessage: ${representativeError.message}\nStack:\n${representativeError.stack || 'No stack trace available'}`;

      return details;
    })
    .join('\n\n---\n\n');
};

export const ERROR_LIST = ['SyntaxError', 'ReferenceError'];
